import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import queryUtils from 'app/shared/utils/queryUtils';
import RequesterActions from 'app/shared/flux/actions/RequesterActions';
import { logError, type LogHttpRequest } from '@zg-rentals/log-error';

type AxiosError = {
  response?: {
    status: number;
    headers?: Record<string, string>;
  };
  request?: unknown;
  message?: string;
  config?: {
    url?: string;
    params?: Record<string, unknown>;
  };
};

// an axiosError, server responded with non-2xx status code
function isApiResponseError(err: unknown): err is AxiosError & { response: NonNullable<AxiosError['response']> } {
  return Boolean((err as AxiosError).response);
}

// an axiosError, request was made but no response received
function isApiRequestError(err: unknown): err is AxiosError & { request: NonNullable<AxiosError['request']> } {
  return Boolean((err as AxiosError).request);
}

type RootState = {
  location: {
    ssrEntry: {
      traceId: string | null;
    };
  };
};

const ErrorActions = {
  errorHandler({
    error,
    errorClass,
    errorLocation,
    httpRequest,
    criticalType = null,
  }: {
    error: AxiosError;
    errorClass: string;
    errorLocation: string;
    req?: unknown;
    httpRequest?: LogHttpRequest;
    criticalType?: null | string;
  }) {
    return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>, getState: () => RootState) => {
      const state = getState();
      const traceId = __SERVER__ ? state.location.ssrEntry.traceId : null;
      const { message, config } = error;

      if (isApiResponseError(error)) {
        const { status, headers } = error.response;

        let xEidResponseHeaders: string | undefined;

        if (__SERVER__ && headers && headers['x-eid']) {
          xEidResponseHeaders = headers['x-eid'];
          dispatch(RequesterActions.pushxEid(xEidResponseHeaders));
        }

        logError({
          error,
          errorType: 'ApiError',
          context: {
            details: 'server responded with status code out of 2xx range',
            errorLocation,
            errorClass,
            traceId,
            url: config?.url,
            params: config?.params,
            fullUrl:
              config?.url && config?.params
                ? `${config.url}${queryUtils.stringify(config.params as Record<string, string | number | boolean>)}`
                : undefined,
            criticalType,
            status,
            message,
          },
          httpRequest,
        });
      } else if (isApiRequestError(error)) {
        // todo: we can use pontoon counters instead for req related failures
        if (message && message.toLowerCase() !== 'request aborted') {
          logError({
            error,
            errorType: 'ApiError_RequestAborted',
            context: {
              details: 'request made, no response received',
              errorLocation,
              errorClass,
              traceId,
              url: config?.url,
              params: config?.params,
              fullUrl:
                config?.url && config?.params
                  ? `${config.url}${queryUtils.stringify(config.params as Record<string, string | number | boolean>)}`
                  : undefined,
              criticalType,
              message,
            },
            httpRequest,
          });
        }
      } else {
        logError({
          error,
          errorType: 'ApiError',
          context: {
            errorLocation,
            errorClass,
            message,
          },
          httpRequest,
        });
      }
    };
  },
};

export default ErrorActions;
